import { ProofWindowPanel } from "./components/ProofWindowPanel";
import { LiveSignalStrip } from "./components/LiveFacts";
import { CostATIS } from "./components/CostATIS";
const rail = [
  ["01", "CONTRACT", "Objective, MUST clauses, scope, budget, rollback"],
  ["02", "GATE", "Capability, effect class, approval, prohibited actions"],
  ["03", "EXECUTE", "One content-bound action through the approved tool"],
  ["04", "OBSERVE", "Before state, result, after state, cost, failures"],
  ["05", "ACCEPT", "Verifier receipts checked against every mandatory clause"],
  ["06", "RELEASE", "SHIPPED only when the acceptance engine closes the contract"],
];

const systems = [
  {
    code: "EC-01",
    id: "EXECUTION CONTRACT",
    state: "LOCAL / TESTED",
    title: "Authority before action",
    body: "A content-addressed mission contract binds objective, resources, prohibited actions, approvals, and completion criteria.",
  },
  {
    code: "PG-02",
    id: "PROOFNATIVE GATE",
    state: "LOCAL / TESTED",
    title: "Policy in the execution path",
    body: "Capability, effect class, budget, preview scope, and production approval are evaluated before a tool can run.",
  },
  {
    code: "ES-03",
    id: "EVIDENCE SPINE",
    state: "LOCAL / TESTED",
    title: "Receipts bind the state change",
    body: "Software-Ed25519 envelopes chain contract, action, observation, verifier, cost, and predecessor hashes.",
  },
  {
    code: "EC-04",
    id: "EDGE CELL",
    state: "LOCAL / TESTED",
    title: "Queue and proof near the work",
    body: "SQLite queueing, replay protection, strict projection, and signing run locally. Hardware attestation is not claimed.",
  },
  {
    code: "PW-05",
    id: "PROOF WINDOW",
    state: "LIVE / CLOUDFLARE",
    title: "Public evidence without the secrets",
    body: "A Worker, D1 store, and durable Workflow accept only canonical signed chains and expose an allowlisted read model.",
  },
  {
    code: "AE-06",
    id: "ACCEPTANCE ENGINE",
    state: "LOCAL / TESTED",
    title: "The model cannot self-approve",
    body: "Only the acceptance engine can issue RELEASE_READY or SHIPPED. Any unmet MUST or SHALL becomes HOLD.",
  },
];

function BrandLockup({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`brand ${compact ? "brand--compact" : ""}`}>
      <img src="/brand/aevion-lockup-black.png" alt="" />
      <span>
        <strong>AEVION</strong>
        <small>PROOFNATIVE</small>
      </span>
    </span>
  );
}

export default function App() {
  return (
    <main className="site-shell">
      <nav className="topbar" aria-label="Primary">
        <a className="topbar__brand" href="#top" aria-label="Aevion ProofNative home">
          <BrandLockup compact />
        </a>
        <div className="topbar__right">
          <span className="live-marker"><i /> CLOUDFLARE EDGE LIVE</span>
          <a href="#rail">THE RAIL</a>
          <a href="#proof-window">PROOF WINDOW</a>
        </div>
      </nav>

      <section className="hero" id="top">
        <img className="hero__art" src="/brand/proofnative-hero-v2.png" alt="A laboratory instrument routes a flight through a verification checkpoint and emits an evidence trail." />
        <div className="hero__shade" />
        <div className="hero__copy">
          <p className="eyebrow"><span>PROOFNATIVE</span> / AEVION RAIL v0.2</p>
          <h1>AI actions need<br /><em>receipts.</em></h1>
          <p className="hero__lede">
            A real execution rail for consequential agent work. Contract the mission,
            gate the tool, observe the effect, verify the obligations, then release or HOLD.
          </p>
          <div className="hero__actions">
            <a href="#rail">TRACE THE RAIL <span>↘</span></a>
            <a href="#proof-window" className="hero__actions--quiet">VIEW PUBLIC PROOF <span>↓</span></a>
          </div>
        </div>
        <aside className="hero__telemetry" aria-label="Live system boundary">
          <div className="telemetry__head"><span>AEVION://PUBLIC-EDGE</span><b>LIVE</b></div>
          <dl>
            <div><dt>WORKER</dt><dd>ACTIVE</dd></div>
            <div><dt>D1 RECEIPT STORE</dt><dd>ACTIVE</dd></div>
            <div><dt>DURABLE WORKFLOW</dt><dd>BOUND</dd></div>
            <div><dt>WRITE INGRESS</dt><dd>AUTHENTICATED</dd></div>
          </dl>
          <p>Sanitized projections only. Raw evidence, credentials, customer data, and private operations stay behind the boundary.</p>
        </aside>
      </section>

      <LiveSignalStrip />

      <section className="manifesto" aria-labelledby="manifesto-title">
        <div className="manifesto__radar" aria-hidden="true">
          <div className="radar__sweep" />
          <i className="radar__point radar__point--one" />
          <i className="radar__point radar__point--two" />
          <i className="radar__point radar__point--three" />
          <span>OBSERVE</span>
        </div>
        <div className="manifesto__copy">
          <p className="eyebrow">BEYOND PROBABILISTIC GUESSWORK</p>
          <h2 id="manifesto-title">The model proposes.<br />The rail decides.</h2>
          <p>
            Aevion does not turn model confidence into certainty. ProofNative makes the operating boundary explicit:
            what was authorized, what actually ran, what changed, what was verified, and why the mission shipped or stopped.
          </p>
          <div className="claim-boundary">
            <span>CLAIM BOUNDARY</span>
            <p>This deployment uses software Ed25519 signatures. It does not claim hardware-rooted attestation, certification, or formal proof of model correctness.</p>
          </div>
        </div>
      </section>

      <section className="sdk-section" aria-labelledby="sdk-title">
        <div className="sdk-section__copy">
          <p className="eyebrow">DEVELOPER SURFACE / LOCAL / TESTED</p>
          <h2 id="sdk-title">One call.<br />No self-approval.</h2>
          <p>
            <code>aevion.ship</code> is a narrow façade over the same contract compiler,
            gate, tool lifecycle, acceptance engine, receipt signer, and projection store.
            Requirements without a named executable verifier are rejected.
          </p>
          <ul>
            <li>Raw action arguments are hashed, not copied into receipts.</li>
            <li>Production actions require content-bound approval.</li>
            <li>A release reference is required to move from RELEASE_READY to SHIPPED.</li>
          </ul>
        </div>
        <div className="sdk-console" aria-label="Aevion ship Python example">
          <div className="sdk-console__head"><span>PYTHON</span><span>AEVION SDK / v0.1</span></div>
          <pre><code><span className="code-key">run</span> = aevion.ship({`\n`}
  goal=<span className="code-string">&quot;deploy the public proof window&quot;</span>,{`\n`}
  must=(Requirement({`\n`}
    clause_id=<span className="code-string">&quot;must-health-200&quot;</span>,{`\n`}
    verifier=<span className="code-string">&quot;cloudflare.live-observer&quot;</span>,{`\n`}
    expected={`{`}<span className="code-string">&quot;health_http_status&quot;</span>: <span className="code-number">200</span>{`}`},{`\n`}
  ),),{`\n`}
  actions=(deploy_action,),{`\n`}
  release_authorization_ref=release_ref,{`\n`}
){`\n\n`}
<span className="code-comment"># The model does not set this.</span>{`\n`}
assert <span className="code-key">run.shipped</span></code></pre>
          <div className="sdk-console__foot"><span>CONTRACT HASH</span><span>ACTION HASH</span><span>RECEIPT CHAIN</span></div>
        </div>
      </section>

      <section className="rail-section" id="rail" aria-labelledby="rail-title">
        <header className="section-title">
          <div><p className="eyebrow">THE PROOFNATIVE RAIL</p><h2 id="rail-title">Contract to consequence.</h2></div>
          <p>One forward path. A failed obligation exits to HOLD, never to a softer definition of success.</p>
        </header>
        <ol className="rail">
          {rail.map(([number, title, detail]) => (
            <li key={number}>
              <span className="rail__number">{number}</span>
              <div className="rail__node"><i /></div>
              <div><strong>{title}</strong><p>{detail}</p></div>
              <span className="rail__state">{number === "06" ? "RELEASE / HOLD" : "BOUND"}</span>
            </li>
          ))}
        </ol>
        <div className="hold-line"><span>FAIL / MISSING / TAMPER</span><strong>→ HOLD</strong><span>NO SILENT RELEASE</span></div>
      </section>

      <section className="atis-section" id="cost-atis" aria-labelledby="atis-title">
        <header className="section-title">
          <div><p className="eyebrow">FOUNDER COST FIREWALL</p><h2 id="atis-title">Cost ATIS</h2></div>
          <p>Microscopic drilldown for every cent. No surprise bills.</p>
        </header>
        <CostATIS />
      </section>

      <section className="system-section" aria-labelledby="system-title">
        <header className="section-title">
          <div><p className="eyebrow">ONE EVIDENCE SPINE</p><h2 id="system-title">Six operating surfaces.</h2></div>
          <p>These are implementation states, not roadmap adjectives. LIVE and LOCAL / TESTED are intentionally different claims.</p>
        </header>
        <div className="system-grid">
          {systems.map((system) => (
            <article key={system.id}>
              <div className="system-card__meta"><span>{system.code}</span><span>{system.state}</span></div>
              <p className="system-card__id">{system.id}</p>
              <h3>{system.title}</h3>
              <p>{system.body}</p>
              <div className="system-card__trace" aria-hidden="true"><i /><i /><i /></div>
            </article>
          ))}
        </div>
      </section>

      <section className="window-section" id="proof-window" aria-labelledby="window-title">
        <header className="section-title section-title--window">
          <div><p className="eyebrow">PUBLIC / SANITIZED / READ ONLY</p><h2 id="window-title">Proof Window</h2></div>
          <p>Rows arrive only after canonical-body, SHA-256, Ed25519, predecessor-chain, and projection-schema checks.</p>
        </header>
        <ProofWindowPanel />
        <div className="semantics">
          <strong>READ THE STATE LITERALLY</strong>
          <p><code>LOCALLY_VERIFIED</code> is not certification, formal proof, or external reproduction. Hardware-backed signing, Hailo classification, and independent reproduction are not implied unless their own receipts are present.</p>
        </div>
      </section>

      <section className="boundary" aria-label="Public release boundary">
        <div><span>PUBLIC</span><p>Website assets · sanitized projections · exact claim ceilings</p></div>
        <div><span>REMAINS GATED</span><p>Customer data · prompts · raw logs · credentials · SBIR drafts · campaign operations · synthetic results</p></div>
      </section>

      <footer>
        <BrandLockup compact />
        <code>CONTRACT → GATE → EXECUTE → OBSERVE → ACCEPT → RELEASE</code>
      </footer>
    </main>
  );
}
